<template>
  <div
    id="channel-wrapper-v2"
    class="relative flex min-h-dvh flex-col transition-colors duration-200"
    :class="{ 'large-desktop:grid': largeDesktop && chatOpen }"
    :style="shellGridStyle"
  >
    <!--
      Top bar region, exposed as the #topbar slot so callers can override it;
      defaults to the relocated ChannelTopBar nav below.
    -->
    <header
      class="channel-topbar sticky top-0 z-50 inset-x-0 backdrop-blur-3xl bg-bg-secondary"
      :class="{
        hidden: hideNavigation,
        'large-desktop:col-span-full': largeDesktop && chatOpen,
      }"
    >
      <slot
        name="topbar"
        :chat-open="chatOpen"
        :toggle-chat="toggleChat"
        :large-desktop="largeDesktop"
      >
        <!--
          The relocated top-bar nav (ACTIVITIES / REWARDS / ANNOUNCEMENTS tabs +
          MESSAGES toggle + member avatars) replacing the legacy right-hand nav
          column. Breakpoint logic (column vs modal) stays in this shell's
          toggleChat; the top bar emits a single toggle-chat intent.
        -->
        <div class="mx-auto w-full" :style="{ maxWidth: maxPageWidth }">
          <ChannelTopBar
            :chat-open="chatOpen"
            :large-desktop="largeDesktop"
            :enable-coupon-offers="enableCouponOffers"
            @toggle-chat="toggleChat"
          />
        </div>
      </slot>
    </header>

    <CommunitiesRow
      v-if="showCommunitiesRow"
      class="channel-top-row hide-scrollbar sticky top-0 z-40 inset-x-0 bg-bg-secondary px-4 py-2 backdrop-blur-3xl"
      :class="{ 'large-desktop:col-span-full': largeDesktop && chatOpen }"
    />
    <ProfileRow
      v-else-if="userStore.isAuthenticated && !largeDesktop && showProfileRow"
      class="channel-top-row hide-scrollbar sticky top-0 z-40 inset-x-0 bg-bg-secondary backdrop-blur-3xl"
      :class="{ 'large-desktop:col-span-full': largeDesktop && chatOpen }"
    />

    <!--
      Page column. Single centered column when chat is closed; left grid column
      when chat is open on large desktop. The page body content stays capped via
      max-width while the wrapper exposes its viewport region width as the
      --channel-page-region-width CSS variable (and a provided ref) so
      descendants can break the QUESTS / REWARDS rails out to the page region's
      edges WITHOUT assuming 100vw.
    -->
    <main
      id="main-content"
      class="channel-page-region flex flex-1 flex-col"
      :class="{ 'large-desktop:col-[1]': largeDesktop && chatOpen }"
      :style="pageRegionStyle"
    >
      <div class="channel-page-view mx-auto flex w-full flex-1 flex-col">
        <RouterView />

        <AppFooter />
      </div>
    </main>

    <!--
      Chat column. Right grid column on large desktop when chat is open. Reuses
      the existing ChannelChat component unchanged (NG6). On mobile / tablet the
      modal path below is used instead.
    -->
    <aside
      v-if="largeDesktop && chatOpen"
      class="chat-column sticky top-0 max-h-dvh overflow-y-auto px-4 pb-6 large-desktop:col-[2]"
    >
      <ChannelChat />
    </aside>
  </div>

  <ChannelChatRoomModal
    v-if="!largeDesktop"
    v-model:is-open="chatRoomModalIsOpen"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, provide, ref, watch } from "vue";
import { useWindowSize } from "@vueuse/core";
import { useUserStore } from "@/stores/user.store";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import {
  CHANNEL_CHAT_COLUMN_WIDTH,
  channelPageRegionWidthKey,
} from "@/composables/channel/use-channel-page-region";
import { useChannelShellRouteVisibility } from "@/composables/channel/use-channel-shell-route-visibility";
import CommunitiesRow from "./communities-row.vue";
import ProfileRow from "./profile-row.vue";
import AppFooter from "../app-footer.vue";
import ChannelChat from "../chat/channel-chat.vue";
import ChannelTopBar from "./channel-top-bar.vue";

// Lazy-loaded: the mobile/tablet chat modal pulls ChannelChat into its own
// chunk so it stays out of the initial channel-shell bundle (the legacy layout
// lazy-loaded this surface too). The desktop chat COLUMN uses the static
// ChannelChat import above because it renders inline in the two-column shell.
const ChannelChatRoomModal = defineAsyncComponent(
  () => import("../chat/channel-chat-room-modal.vue"),
);

const userStore = useUserStore();
const { showCommunitiesRow, hideNavigation, showProfileRow } =
  useChannelShellRouteVisibility();
const { largeDesktop } = useResponsiveHelpers();
const { getBoolean } = useFeatureFlags();
const { width: viewportWidth } = useWindowSize();

// Whether the Offers/Rewards surface is enabled. The top bar uses this to drop
// the "Rewards" tab (whose #offers scroll anchor does not exist when offers are
// hidden, which would otherwise produce a dead scroll target). Mirrors the
// `enableCouponOffers` gate channel.vue applies to <OffersSection>.
const enableCouponOffers = getBoolean("enable_coupon_offers");

/**
 * Net-new component-local chat-open state. chat.store has NO desktop open/close
 * toggle, so this stays component-local per the Vue state rules — it is NOT
 * added to a Pinia store. Toggled by the MESSAGES control on the top bar (or
 * the default top-bar fallback above).
 */
const chatOpen = ref(false);
const chatRoomModalIsOpen = ref(false);

function toggleChat() {
  if (largeDesktop.value) {
    chatOpen.value = !chatOpen.value;
    return;
  }
  // On mobile / tablet the chat opens via the existing modal, exactly as the
  // legacy MobileChannelFooter click did.
  chatRoomModalIsOpen.value = true;
}

// If the viewport collapses below large-desktop while chat is open, fall back
// to the single-column model (the modal path handles narrow widths).
watch(largeDesktop, (isLargeDesktop) => {
  if (!isLargeDesktop) chatOpen.value = false;
});

// Centered content-column cap. Matches the AS-Monaco Figma content width
// (1136px), decoupled from the 820px `desktop` breakpoint it formerly reused.
// The full-bleed rails still bleed to the full viewport via `pageRegionWidth`;
// only the centered body (profile card, leaderboard, headers, threads) caps here.
const maxPageWidth = "1136px";

/**
 * Effective horizontal region the page column occupies within the viewport.
 * - chat closed / mobile: the full viewport width.
 * - chat open on large desktop: the viewport minus the sticky chat column width.
 *
 * Exposed BOTH as the --channel-page-region-width CSS variable (so a pure-CSS
 * rail breakout can reference it) and as a provided reactive ref (so a
 * descendant can choose). The rail breakout is computed from this region
 * width, NOT from 100vw, so the rails bleed to the page region's edges and
 * never under the chat column in the two-column state.
 */
const pageRegionWidth = computed(() => {
  if (largeDesktop.value && chatOpen.value) {
    return Math.max(viewportWidth.value - CHANNEL_CHAT_COLUMN_WIDTH, 0);
  }
  return viewportWidth.value;
});

const pageRegionWidthCss = computed(() => `${pageRegionWidth.value}px`);

provide(channelPageRegionWidthKey, pageRegionWidth);

const shellGridStyle = computed(() =>
  largeDesktop.value && chatOpen.value
    ? {
        gridTemplateColumns: `minmax(0, 1fr) ${CHANNEL_CHAT_COLUMN_WIDTH}px`,
      }
    : undefined,
);

const pageRegionStyle = computed(() => ({
  "--channel-page-region-width": pageRegionWidthCss.value,
  "--channel-page-max-width": maxPageWidth,
}));
</script>

<style scoped>
#channel-wrapper-v2 {
  background: var(--bg);
  color: var(--text);
}

.channel-page-region {
  /* Default region width fallback for SSR / first paint before measurement. */
  --channel-page-region-width: 100vw;
  --channel-page-max-width: v-bind(maxPageWidth);
  /*
   * Contain the full-bleed QUESTS / REWARDS rail breakout exactly at the
   * page-column boundary. `overflow-x: clip` is declared HERE —
   * on the same element that hosts --channel-page-region-width — so the
   * region-width-bled rails never produce a horizontal document scrollbar.
   *
   * It is intentionally `clip` (not `auto`/`hidden`): clip does not establish a
   * scroll container, so it neither adds a scroll gutter nor breaks
   * `position: sticky` on descendants. The horizontal-only axis (`overflow-y`
   * left visible) keeps the page body's natural document scroll and lets any
   * popovers/menus inside the region overflow vertically. We do NOT port the
   * legacy `#main-content` `overflow-x: auto` (`isPageContainerHozScrollable`)
   * behavior into the v2 shell — that auto ancestor would clip the breakout
   * into a scroll gutter instead of a clean edge-to-edge rail.
   */
  overflow-x: clip;
}

.channel-page-view {
  max-width: var(--channel-page-max-width);
}
</style>
